import { faClose, faTicket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import { BookerContext, BookerFormValues } from '../Booker'
import { useFormContext, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import Field from './Field'
import { cn, cnWithPrefix } from 'ui/lib/utils'

const PromoCodeIcon = ({ className }: { className?: string }) => {
   const { useTailwindPrefix } = useContext(BookerContext)
   const customCn = useTailwindPrefix ? cnWithPrefix('tw-') : cn

   return (
      <svg
         className={customCn(
            {
               'h-[1em] overflow-visible box-content': !useTailwindPrefix,
               'tw-h-[1em] tw-overflow-visible tw-box-content': useTailwindPrefix,
            },
            className,
         )}
         viewBox="0 0 24 24"
         fill="none"
         xmlns="http://www.w3.org/2000/svg"
      >
         <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M21.7498 17.85V15.25C20.0498 14.85 19.1998 13.8333 19.1998 12.2C19.1998 10.5666 20.0498 9.49995 21.7498 8.99995V6.19995H2.2998V8.99995C4.06647 9.29995 4.9498 10.3666 4.9498 12.2C4.9498 14.0333 4.06647 15.05 2.2998 15.25V17.85H21.7498Z"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
         />
         <path
            d="M9.96698 9.64453C9.08148 9.64453 8.54248 10.2451 8.54248 11.2461C8.54248 12.2548 9.08148 12.8631 9.96698 12.8631C10.8525 12.8631 11.3915 12.2548 11.3915 11.2461C11.3915 10.2451 10.8525 9.64453 9.96698 9.64453ZM13.5244 9.65993L9.55888 15.0499H10.4136L14.3714 9.65993H13.5244ZM9.96698 10.1681C10.4983 10.1681 10.8063 10.5608 10.8063 11.2461C10.8063 11.9391 10.4983 12.3395 9.96698 12.3395C9.43568 12.3395 9.13538 11.9391 9.13538 11.2461C9.13538 10.5608 9.43568 10.1681 9.96698 10.1681ZM14.0249 11.8544C13.1394 11.8544 12.6004 12.455 12.6004 13.456C12.6004 14.4647 13.1394 15.0653 14.0249 15.0653C14.9104 15.0653 15.4494 14.4647 15.4494 13.456C15.4494 12.455 14.9104 11.8544 14.0249 11.8544ZM14.0249 12.3703C14.5562 12.3703 14.8642 12.7707 14.8642 13.456C14.8642 14.149 14.5562 14.5417 14.0249 14.5417C13.4936 14.5417 13.1933 14.149 13.1933 13.456C13.1933 12.7707 13.4936 12.3703 14.0249 12.3703Z"
            fill="currentColor"
         />
      </svg>
   )
}

const PromotionCodeField = () => {
   const { control, setValue, register, setFocus } = useFormContext<BookerFormValues>()
   const { useTailwindPrefix, version, vertical, showLabels } = useContext(BookerContext)
   const promotionCode = useWatch({ control, name: 'promotionCode' })
   const intl = useIntl()
   function reset() {
      setValue('promotionCode', '')
      setValue('loyaltyPlan.promotionCode', undefined)
   }

   function focusInput() {
      setFocus('promotionCode')
   }

   return (
      <Field className="relative tw-relative">
         <div
            className={classNames({
               'flex z-10 justify-center w-4 -rotate-45': !useTailwindPrefix,
               'tw-flex tw-z-10 tw-justify-center tw-w-4 -tw-rotate-45': useTailwindPrefix,
            })}
            onClick={focusInput}
         >
            {version === 'padded' ? <PromoCodeIcon /> : <FontAwesomeIcon icon={faTicket} size="sm" />}
         </div>
         <div
            className={classNames({
               'relative ': !useTailwindPrefix,
               'tw-relative': useTailwindPrefix,
            })}
         >
            {version === 'padded' && showLabels && !vertical && (
               <div
                  className={classNames({
                     'hidden absolute md:flex inset-0 text-widget-pill-a11y/75': !useTailwindPrefix,
                     'tw-hidden tw-absolute md:tw-flex tw-inset-0 tw-text-widget-pill-a11y/75': useTailwindPrefix,
                  })}
               >
                  <div
                     className={classNames({
                        'text-xs truncate': !useTailwindPrefix,
                        'tw-text-xs tw-truncate': useTailwindPrefix,
                     })}
                  >
                     {intl.formatMessage({ id: `booker.promotion.code${promotionCode ? '' : '.label'}` })}
                  </div>
               </div>
            )}
            <input
               className={classNames({
                  'bg-transparent z-10 relative md:placeholder:text-widget-pill-a11y/90':
                     version === 'padded' && !vertical && !useTailwindPrefix,
                  'tw-bg-transparent tw-z-10 tw-relative md:placeholder:tw-text-widget-pill-a11y/90':
                     version === 'padded' && !vertical && useTailwindPrefix,
                  'focus:outline-none w-11/12 overflow-hidden text-ellipsis': !useTailwindPrefix,
                  'focus:tw-outline-none tw-w-11/12 tw-overflow-hidden tw-text-ellipsis': useTailwindPrefix,
               })}
               type="text"
               {...register('promotionCode')}
               placeholder={intl.formatMessage({
                  id: `booker.promotion.code${version === 'padded' && showLabels && !vertical ? '.short' : ''}`,
               })}
            />
         </div>
         <div
            className={classNames({
               'flex z-10 justify-center w-4': !useTailwindPrefix,
               'tw-flex tw-z-10 tw-justify-center tw-w-4': useTailwindPrefix,
               hidden: !promotionCode && !useTailwindPrefix,
               'tw-hidden': !promotionCode && useTailwindPrefix,
            })}
            onClick={reset}
         >
            <FontAwesomeIcon icon={faClose} />
         </div>
      </Field>
   )
}

export default PromotionCodeField
